import { type ReactNode } from 'react';
import { UserSubscriptionType } from '../services/subscriptions/subscriptions.types';
import { UserPlanType } from '../hooks/useUserSubscription/useUserSubscription.types';
import type { Entitlement } from '@frontegg/react';

// `getFeatureEntitlement` / `getPermissionEntitlement` are plain functions, not
// hooks. Storing hooks on a context value violates React Compiler rules; see
// `userEntitlementsContext.tsx` for the wrapper hooks consumers should use.
export type UserEntitlementsData = {
  plan: UserSubscriptionType | undefined;
  isPaidUser: boolean;
  userPlanType: UserPlanType | undefined;
  getFeatureEntitlement: (featureKey: FronteggFeatureKeys) => Entitlement;
  getPermissionEntitlement: (
    permissionKey: FronteggPermissionKeys,
  ) => Entitlement;
};

export type UserEntitlementsContextType = UserEntitlementsData & {
  // `false` until the authenticated entitlements have hydrated, so consumers can
  // distinguish "not yet loaded" from "genuinely not entitled" (PROD-9155).
  // Always `true` on the patient branch, where the stub resolves synchronously.
  areEntitlementsReady: boolean;
};

export enum FronteggFeatureKeys {
  ProPlan = 'pro-plan-feature', // DEPRECATED - use specific entitlement checks instead
  PremiumPlan = 'premium-plan-feature', // DEPRECATED - use specific entitlement checks instead
  DayPass = 'day-pass-feature',
  SubscriptionManagement = 'subscription-management-feature',
  TrustedCustomer = 'trusted-customer-feature',
  CustomRoomInvite = 'custom-room-invite-feature',
  ProPlanSubscription = 'pro-plan-subscription-feature', // DEPRECATED - use specific entitlement checks instead
  PremiumPlanSubscription = 'premium-plan-subscription-feature', // DEPRECATED - use specific entitlement checks instead
  Passcode = 'passcode-feature',
  Branding = 'branding-feature',
  PatientTransfer = 'patient-transfer-feature',
  DialOut = 'dial-out-feature',
  CanSharePersonalRoom = 'share-personal-rooms-feature',
  CreateSharedRooms = 'create-shared-rooms-feature',
  SharedRoomsAnalytics = 'shared-rooms-analytics-feature',
  SharedRoomsHistory = 'shared-rooms-history-feature',
  InviteUsersWithAccountCreation = 'invite-with-account-creation-feature',
  ExportUsers = 'export-users-feature',
  TenantBaaManagement = 'tenant-baa-management',
  CustomWaitingRoom = 'custom-waiting-room-feature',
  AppsSettings = 'apps-settings-feature',
  // DO NOT USE THIS. It is to support backwards compatibility with the isPaid check in v2. Use specific feature checks instead
  LegacyIsPaid = 'legacy-ispaid-feature',
}

export enum FronteggPermissionKeys {
  InviteUser = 'invite-users-permission',
  CancelSubscription = 'cancel-subscription-permission',
  UpgradeSubscription = 'upgrade-subscription-permission',
  ViewBillingPortal = 'view-billing-portal',
  ViewSubscriptionTab = 'view-subscription-tab',
  UpdateTenantSettings = 'update-tenant-settings',
  CanManageApps = 'can-manage-apps',
  AnalyticsAllRooms = 'analytics-all-rooms-permission',
  HistoryAllRooms = 'history-all-rooms-permission',
  ViewSeatsBanner = 'view-seats-banner',
  ManageTenantRooms = 'manage-tenant-rooms',
  ManageBranding = 'manage-branding',
  CreateTenantBaa = 'create-tenant-baa',
  DeleteTenantBaa = 'delete-tenant-baa',
}

export type InternalProviderProps = {
  children: ReactNode;
};

export type UserEntitlementsProviderProps = InternalProviderProps & {
  isAuthenticated: boolean;
};
